// Understory homepage behaviors: the hero's rotating word and the overlay header states.

const UNDERSTORY_REDUCED_MOTION = '(prefers-reduced-motion: reduce)';
const UNDERSTORY_ROTATE_MS = 2200;
const UNDERSTORY_SCROLLED_AFTER = 40;

if (!customElements.get('text-rotator')) {
  class TextRotator extends HTMLElement {
    connectedCallback() {
      const words = (this.dataset.words || '')
        .split(',')
        .map((word) => word.trim())
        .filter(Boolean);

      // Leave the server-rendered full sentence alone when there is nothing to rotate,
      // or when the visitor asked for less motion.
      if (words.length < 2 || window.matchMedia(UNDERSTORY_REDUCED_MOTION).matches) return;

      this.words = words;
      this.index = 0;
      this.onScreen = true;
      this.show(this.words[0]);

      this.onVisibility = () => this.sync();
      document.addEventListener('visibilitychange', this.onVisibility);

      if ('IntersectionObserver' in window) {
        this.observer = new IntersectionObserver((entries) => {
          this.onScreen = entries[0].isIntersecting;
          this.sync();
        });
        this.observer.observe(this);
      }

      this.sync();
    }

    disconnectedCallback() {
      this.stop();
      if (this.observer) this.observer.disconnect();
      if (this.onVisibility) document.removeEventListener('visibilitychange', this.onVisibility);
    }

    // A fresh span each swap re-triggers the CSS rise animation. textContent only, never markup.
    show(word) {
      const span = document.createElement('span');
      span.textContent = word;
      this.replaceChildren(span);
    }

    sync() {
      if (this.onScreen && !document.hidden) {
        this.start();
      } else {
        this.stop();
      }
    }

    start() {
      if (this.timer) return;
      this.timer = window.setInterval(() => {
        this.index = (this.index + 1) % this.words.length;
        this.show(this.words[this.index]);
      }, UNDERSTORY_ROTATE_MS);
    }

    stop() {
      window.clearInterval(this.timer);
      this.timer = null;
    }
  }

  customElements.define('text-rotator', TextRotator);
}

if (!customElements.get('understory-hero')) {
  class UnderstoryHero extends HTMLElement {
    connectedCallback() {
      const video = this.querySelector('video');
      if (video && window.matchMedia(UNDERSTORY_REDUCED_MOTION).matches) {
        video.removeAttribute('autoplay');
        video.pause();
      }

      // The overlay header only exists on the homepage (header.liquid sets data-header-overlay).
      this.header = document.querySelector('.section-header');
      if (!this.header || !this.header.querySelector('[data-header-overlay]')) return;

      this.frame = null;
      this.onScrollOrResize = () => {
        if (this.frame) return;
        this.frame = window.requestAnimationFrame(() => {
          this.frame = null;
          this.updateHeader();
        });
      };

      window.addEventListener('scroll', this.onScrollOrResize, { passive: true });
      window.addEventListener('resize', this.onScrollOrResize, { passive: true });
      this.updateHeader();
    }

    disconnectedCallback() {
      if (this.onScrollOrResize) {
        window.removeEventListener('scroll', this.onScrollOrResize);
        window.removeEventListener('resize', this.onScrollOrResize);
      }
      if (this.frame) window.cancelAnimationFrame(this.frame);
      if (this.header) this.header.classList.remove('section-header--scrolled', 'section-header--on-light');
    }

    updateHeader() {
      this.header.classList.toggle('section-header--scrolled', window.scrollY > UNDERSTORY_SCROLLED_AFTER);

      // Whichever light section spans the header's bottom edge decides the header theme.
      const probe = this.header.getBoundingClientRect().bottom;
      // In the A/B test's light arm (html.ab-light), sections marked data-ab-header-theme also count as light.
      const onLight = Array.from(
        document.querySelectorAll('[data-header-theme="light"], html.ab-light [data-ab-header-theme="light"]')
      ).some((el) => {
        const rect = el.getBoundingClientRect();
        return rect.top <= probe && rect.bottom >= probe;
      });
      this.header.classList.toggle('section-header--on-light', onLight);
    }
  }

  customElements.define('understory-hero', UnderstoryHero);
}
